import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  message,
  Modal,
  Tooltip,
  Typography,
  Row,
  Col,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import customerJourneyService from '../../services/customerJourneyService';
import JourneyNodeTimeline from './components/JourneyNodeTimeline';

const { Title } = Typography;
const { confirm } = Modal;

const CustomerJourneyList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [journeys, setJourneys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `共 ${total} 条记录，显示第 ${range[0]}-${range[1]} 条`
  });

  // 搜索条件
  const [searchParams, setSearchParams] = useState({
    keyword: ''
  });


  // 加载数据
  const loadJourneys = async (params = {}) => {
    try {
      setLoading(true);
      
      const queryParams = {
        page: (params.current || pagination.current) - 1,
        size: params.pageSize || pagination.pageSize,
        keyword: searchParams.keyword
      };

      // 过滤空值
      Object.keys(queryParams).forEach(key => {
        if (!queryParams[key] && queryParams[key] !== 0) {
          delete queryParams[key];
        }
      });

      const response = await customerJourneyService.getJourneyList(queryParams);

      if (response.data.success) {
        setJourneys(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          current: (response.data.currentPage || 0) + 1,
          total: response.data.totalElements || 0,
          pageSize: response.data.size || 20
        }));
      }
    } catch (error) {
      console.error('加载旅程列表失败:', error);
      message.error('加载旅程列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJourneys();
  }, []);

  // 搜索处理
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadJourneys({ current: 1 });
  };

  // 重置搜索
  const handleReset = () => {
    setSearchParams({
      keyword: ''
    });
    setPagination(prev => ({ ...prev, current: 1 }));
    setTimeout(() => {
      loadJourneys({ current: 1 });
    }, 100);
  };

  // 创建旅程
  const handleCreate = () => {
    navigate('/customer-journey/create');
  };

  // 查看详情
  const handleView = (record) => {
    navigate(`/customer-journey/detail/${record.id}`);
  };

  // 编辑旅程
  const handleEdit = (record) => {
    navigate(`/customer-journey/edit/${record.id}`);
  };

  // 删除旅程
  const handleDelete = async (record) => {
    try {
      await customerJourneyService.deleteJourney(record.id);
      message.success('旅程删除成功');
      loadJourneys();
    } catch (error) {
      console.error('删除旅程失败:', error);
      message.error('删除旅程失败');
    }
  };


  // 同步到企微
  const handleSync = async (record) => {
    try {
      await customerJourneyService.syncJourneyToWework(record.id);
      message.success('旅程同步成功');
      loadJourneys();
    } catch (error) {
      console.error('同步旅程失败:', error);
      message.error('同步旅程失败');
    }
  };

  // 自定义渲染行
  const renderJourneyRow = (record) => (
    <div style={{ padding: '16px 0' }}>
      {/* 旅程名称和操作 */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        marginBottom: '12px',
        gap: '8px'
      }}>
        <div style={{ 
          fontSize: '16px', 
          fontWeight: '500',
          color: '#262626'
        }}>
          {record.journeyName}
        </div>
        <Tooltip title="编辑旅程">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#1677ff' }}
          />
        </Tooltip>
        <Popconfirm
          title="确定要删除这个旅程吗？"
          description="删除后无法恢复，包括所有阶段和关联数据。"
          onConfirm={() => handleDelete(record)}
          okText="确定"
          cancelText="取消"
        >
          <Tooltip title="删除旅程">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
            />
          </Tooltip>
        </Popconfirm>
      </div>
      
      {/* 时间线 */}
      <JourneyNodeTimeline nodes={record.nodes || []} />
    </div>
  );

  // 表格列配置 - 使用单列显示
  const columns = [
    {
      title: '',
      key: 'journey',
      render: (_, record) => renderJourneyRow(record)
    }
  ];

  // 表格变化处理
  const handleTableChange = (paginationConfig) => {
    const params = {
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    };
    loadJourneys(params);
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>客户旅程</Title>
        </Col>
      </Row>
      
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Input
                  placeholder="搜索旅程名称或编码"
                  value={searchParams.keyword}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, keyword: e.target.value }))}
                  onPressEnter={handleSearch}
                  allowClear
                  style={{ width: 250 }}
                />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  搜索
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                >
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                创建旅程
              </Button>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={journeys}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="id"
          showHeader={false}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default CustomerJourneyList;